// ============================================================
// WeatherStrip — 7-day forecast tiles (Oct 27)
//
// Phone-widget style: one tile per day with the sky, high / low and the
// daytime (work-hours) chance of rain. Sits on the Sign Schedule and the
// Tagging Schedule. The tile for the day being planned gets a ring so the
// manager sees that day's rain chance while building it.
//
// Data comes from GET /api/weather (NWS, cached 30 min server-side). If
// the forecast can't load at all the strip simply isn't there — it must
// never get in the way of the board.
// ============================================================
import { useState, useEffect, useCallback } from 'react';
import { api } from '../../api';
import { useIsMobile } from '../../mobile';

const REFRESH_MS = 30 * 60 * 1000;
const centralToday = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Chicago' }).format(new Date());

// NWS short forecasts are plain English ("Chance Showers And
// Thunderstorms", "Mostly Sunny") — read the words, pick the picture.
function skyIcon(text = '') {
  const t = text.toLowerCase();
  if (t.includes('thunder')) return '⛈️';
  if (/snow|flurr|sleet|freezing|ice|wintry/.test(t)) return '🌨️';
  if (/rain|shower|drizzle/.test(t)) return t.includes('slight chance') ? '🌦️' : '🌧️';
  if (/fog|haze|smoke|dust/.test(t)) return '🌫️';
  if (/mostly cloudy|considerable clouds/.test(t)) return '🌥️';
  if (/partly sunny|partly cloudy/.test(t)) return '⛅';
  if (/cloudy|overcast/.test(t)) return '☁️';
  if (/mostly sunny|mostly clear/.test(t)) return '🌤️';
  if (/sunny|clear|fair/.test(t)) return '☀️';
  if (/wind|breez|blustery/.test(t)) return '💨';
  return '🌤️';
}

// Rain pill gets louder as the chance climbs — 50%+ is the one a manager
// should notice before loading a truck.
function rainStyle(pct) {
  if (pct >= 50) return { background: '#fff', color: '#1d4ed8', fontWeight: 800 };
  if (pct >= 30) return { background: 'rgba(255,255,255,0.28)', color: '#fff', fontWeight: 700 };
  return { background: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.85)', fontWeight: 600 };
}

function fmtTime(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('en-US', { timeZone: 'America/Chicago', hour: 'numeric', minute: '2-digit' });
}

export default function WeatherStrip({ selectedDate }) {
  const isMobile = useIsMobile();
  const [wx, setWx] = useState(null);
  const [failed, setFailed] = useState(false);
  const [editing, setEditing] = useState(false);
  const [zip, setZip] = useState('');
  const [saving, setSaving] = useState(false);
  const [zipError, setZipError] = useState('');

  const load = useCallback(() => {
    api.getWeather()
      .then(d => { setWx(d); setFailed(false); })
      .catch(() => setFailed(true));
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => clearInterval(t);
  }, [load]);

  const saveZip = async (value) => {
    setSaving(true); setZipError('');
    try {
      await api.setWeatherZip(value);
      setEditing(false);
      setWx(null);
      load();
    } catch (e) {
      setZipError(e.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  // Never loaded and can't load → stay out of the way.
  if (failed && !wx) return null;

  const today = centralToday();
  const days = wx?.days || [];

  return (
    <div style={{
      background: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 50%, #38bdf8 100%)',
      color: '#fff', borderRadius: 18, padding: isMobile ? '12px 12px 14px' : '14px 16px 16px', marginBottom: 16,
      boxShadow: '0 6px 18px rgba(37, 99, 235, 0.22)',
    }}>
      {/* ── Header: where + when ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
        {editing ? (
          <form onSubmit={e => { e.preventDefault(); saveZip(zip.trim()); }}
            style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, fontWeight: 700 }}>📍 Zip</span>
            <input autoFocus value={zip} inputMode="numeric" maxLength={5} placeholder="76102"
              onChange={e => setZip(e.target.value.replace(/\D/g, ''))}
              style={{ width: 84, fontSize: 16, padding: '5px 9px', borderRadius: 8, border: 'none', color: '#0f172a' }} />
            <button type="submit" disabled={saving || zip.length !== 5}
              style={{ background: '#fff', color: '#1d4ed8', border: 'none', borderRadius: 8, padding: '6px 12px', fontWeight: 800, fontSize: 13, cursor: 'pointer', opacity: zip.length === 5 ? 1 : 0.6 }}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button type="button" onClick={() => { setEditing(false); setZipError(''); }}
              style={{ background: 'transparent', color: '#fff', border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}>
              Cancel
            </button>
            {wx?.location?.zip && (
              <button type="button" disabled={saving} onClick={() => saveZip('')}
                style={{ background: 'transparent', color: 'rgba(255,255,255,0.8)', border: 'none', fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }}>
                Use shop address instead
              </button>
            )}
            {zipError && <span style={{ fontSize: 12, fontWeight: 700, background: '#fee2e2', color: '#991b1b', padding: '3px 8px', borderRadius: 6 }}>{zipError}</span>}
          </form>
        ) : (
          <button type="button" title="Change the forecast zip code"
            onClick={() => { setZip(wx?.location?.zip || ''); setEditing(true); }}
            style={{ background: 'transparent', border: 'none', color: '#fff', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, fontWeight: 800, letterSpacing: '-0.01em' }}>
            📍 {wx?.location?.label || 'Loading forecast…'}
            {wx?.location?.zip && <span style={{ fontWeight: 600, opacity: 0.8 }}>{wx.location.zip}</span>}
            <span style={{ fontSize: 12, opacity: 0.7, fontWeight: 600 }}>✎</span>
          </button>
        )}
        {!editing && wx && (
          <span style={{ fontSize: 11, fontWeight: 600, opacity: wx.stale ? 1 : 0.75 }}>
            {wx.stale
              ? `⚠ Couldn't refresh — showing the ${fmtTime(wx.updated_at || wx.fetched_at)} forecast`
              : `7-day forecast · Updated ${fmtTime(wx.updated_at || wx.fetched_at)}`}
          </span>
        )}
      </div>

      {/* ── Tiles ── */}
      <div role="list" style={isMobile
        ? { display: 'flex', flexWrap: 'nowrap', gap: 8, overflowX: 'auto', scrollSnapType: 'x mandatory', margin: '0 -4px', padding: '2px 4px', WebkitOverflowScrolling: 'touch' }
        : { display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 10 }}>
        {(days.length ? days : Array.from({ length: 7 }, (_, i) => ({ date: `ph-${i}`, placeholder: true }))).map(d => {
          if (d.placeholder) {
            return <div key={d.date} style={{ minWidth: isMobile ? 68 : 0, height: 132, borderRadius: 14, background: 'rgba(255,255,255,0.10)' }} />;
          }
          const isToday = d.date === today;
          const sel = d.date === selectedDate;
          const dt = new Date(`${d.date}T12:00:00`);
          const dayName = isToday ? 'Today' : dt.toLocaleDateString('en-US', { weekday: 'short' });
          const rain = d.rain ?? 0;
          const tip = [
            `${dt.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}: ${d.detail || d.short || ''}`,
            d.lo != null ? `Night: low ${d.lo}°, ${d.short_night || ''}${d.rain_night ? ` (${d.rain_night}% rain)` : ''}` : null,
          ].filter(Boolean).join('\n');
          return (
            <div key={d.date} role="listitem" title={tip}
              aria-label={`${dayName}: high ${d.hi}°${d.lo != null ? `, low ${d.lo}°` : ''}, ${rain}% chance of rain, ${d.short}`}
              style={{
                minWidth: isMobile ? 68 : 0, flex: isMobile ? '0 0 auto' : undefined, scrollSnapAlign: 'start',
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                padding: '10px 6px 10px', borderRadius: 14,
                background: sel ? 'rgba(255,255,255,0.30)' : 'rgba(255,255,255,0.14)',
                boxShadow: sel ? 'inset 0 0 0 2px #fff' : 'none',
              }}>
              <div style={{ fontSize: 13, fontWeight: 800, lineHeight: 1.1 }}>{dayName}</div>
              <div style={{ fontSize: 11, fontWeight: 600, opacity: 0.7, lineHeight: 1 }}>{dt.getMonth() + 1}/{dt.getDate()}</div>
              <div style={{ fontSize: 28, lineHeight: 1.15, margin: '2px 0' }} aria-hidden="true">{skyIcon(d.short)}</div>
              <div style={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'baseline', gap: 5, lineHeight: 1 }}>
                <span style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.02em' }}>{d.hi}°</span>
                {d.lo != null && <span style={{ fontSize: 13, fontWeight: 600, opacity: 0.7 }}>{d.lo}°</span>}
              </div>
              <div style={{ ...rainStyle(rain), fontSize: 12, padding: '3px 8px', borderRadius: 999, marginTop: 2, whiteSpace: 'nowrap' }}>
                💧 {rain}%
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
